import { type FC } from "react";

import { LoadingSpinner } from "widgets";

import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { type IPageHeaderProps } from "../core/navigation-panel/PageHeader.js";
import ContigSummaryView from "../entities/ContigSummaryView.js";
import ProteinSummaryView from "../entities/ProteinSummaryView.js";
import SequenceSummaryView from "../entities/SequenceSummaryView.js";
import TranscriptSummaryView from "../entities/TranscriptSummaryView.js";
import { type BlastHit, type BlastJob, type JobResult } from "@/graphql.ts";
import { ModulesIcons } from "@/settings/assets-settings.ts";
import { SEQUENCE_TYPE } from "@/settings/settings.js";

interface IBlastResultHitSummaryViewProps {
  job: BlastJob;
  isLoading: boolean;
  jobResult: JobResult;
  hitNum: number;
}

const BlastResultHitSummaryView: FC<IBlastResultHitSummaryViewProps> = (props) => {
  if (!props.jobResult || props.isLoading) {
    return <LoadingSpinner inCenter />;
  }

  if (!props.jobResult.blastHits) {
    return <ContentPlaceholder>No hits found</ContentPlaceholder>;
  }

  // Fake Blast tags. See BlastPageHeader.
  const headerTags = [<img src={ModulesIcons.BLAST.large} width={77} height={44} key={"blast-summary-view-tag-1"} />];

  const hit: BlastHit = props.jobResult.blastHits[props.hitNum ? props.hitNum - 1 : 0];

  const pageHeaderProps: IPageHeaderProps = {
    tags: headerTags,
    project: props.job.event?.project,
  };

  switch (hit.target.type.name) {
    case SEQUENCE_TYPE.GENOMIC:
      return (
        <ContigSummaryView
          contigId={hit.target.id}
          pageHeaderProps={pageHeaderProps}
          title={`Hit ${props.hitNum}`}
          blastContext={true}
        />
      );
    case SEQUENCE_TYPE.TRANSCRIPT:
      return (
        <TranscriptSummaryView
          transcriptId={hit.target.id}
          pageHeaderProps={pageHeaderProps}
          title={`Hit ${props.hitNum}`}
          blastContext={true}
        />
      );
    case SEQUENCE_TYPE.PROTEIN:
      return (
        <ProteinSummaryView
          proteinId={hit.target.id}
          pageHeaderProps={pageHeaderProps}
          title={`Hit ${props.hitNum}`}
          blastContext={true}
        />
      );
    case SEQUENCE_TYPE.OTHER_DNA_RNA:
    case SEQUENCE_TYPE.PROBE:
      return (
        <SequenceSummaryView
          sequenceId={hit.target.id}
          pageHeaderProps={pageHeaderProps}
          title={`Hit ${props.hitNum}`}
        />
      );
    default: {
      console.error("Sequence type " + hit.target.type.name + " is not supported in the BLAST Hit summary view");
      return null;
    }
  }
};

export { BlastResultHitSummaryView };
